import { TrendLabel } from '@/components/customComponents'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { lensMenuCardColors } from '@/constants/COLOR_PALETTE'
import { IAbstractLensItem } from '@/types'
import { formatNumber } from '@/utils'
import { getMetricLabel, getMetricType } from '@/utils/labels'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface ScalrBoxAccordionTableProps<T = any> {
  data: IAbstractLensItem<T>[]
  onClick?: (item: string, skuItem?: string) => void // Updated to handle both string and object
  onSubCategoryClick?: (item: IAbstractLensItem<T>) => void // For loading Level 3 data
  selectedItem?: string | null
  colorKey?: string
  loading?: boolean
  expandedItems?: Record<string, IAbstractLensItem<any>[]> // Level 3 data for expanded items
  isThreeLevel?: boolean // Flag to enable 3-level functionality
  getSubItemAlertCount?: (subItemName: string) => {
    total: number
    unread: number
    alerts: any[]
  }
}

export default function ScalrBoxAccordionTable<T = any>({
  data,
  onClick,
  onSubCategoryClick,
  selectedItem = null,
  colorKey = 'blue',
  loading = false,
  expandedItems = {},
  isThreeLevel = false,
  getSubItemAlertCount,
}: ScalrBoxAccordionTableProps<T>) {
  const { t } = useTranslation()
  const colors = lensMenuCardColors[colorKey] || lensMenuCardColors.blue
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  // Get all unique metric keys from all data items
  const allMetricKeys = useMemo(() => {
    const keysSet = new Set<string>()
    data.forEach((item) => {
      if (item.current) {
        Object.keys(item.current).forEach((key) => keysSet.add(key))
      }
    })
    return Array.from(keysSet)
  }, [data])

  const handleDetailClick = (
    item: string,
    e: React.MouseEvent,
    skuItem?: string
  ) => {
    e.stopPropagation()
    onClick?.(item, skuItem)
  }

  const handleExpandClick = (
    item: IAbstractLensItem<T>,
    e: React.MouseEvent
  ) => {
    e.stopPropagation()
    const itemName = item.item || ''
    const newExpandedRows = new Set(expandedRows)

    if (expandedRows.has(itemName)) {
      newExpandedRows.delete(itemName)
    } else {
      newExpandedRows.add(itemName)
      // Load Level 3 data if not already loaded
      if (!expandedItems[itemName]) {
        onSubCategoryClick?.(item)
      }
    }

    setExpandedRows(newExpandedRows)
  }

  const isRowExpanded = (itemName: string) => expandedRows.has(itemName)

  return (
    <div className="w-full">
      <div className="relative border rounded-lg overflow-hidden">
        {/* Scrollable container with max height for 4 rows + header */}
        <div className="max-h-80 overflow-y-auto">
          <Table
            className={`w-full transition-all duration-200 ${loading ? 'blur-sm' : ''}`}
          >
            {/* Sticky Header */}
            <TableHeader>
              <TableRow>
                <TableHead></TableHead>
                {allMetricKeys.map((metricKey) => (
                  <TableHead key={metricKey}>
                    {getMetricLabel(metricKey, t)}
                  </TableHead>
                ))}
                <TableHead>{t('table.details')}</TableHead>
              </TableRow>
            </TableHeader>

            {/* Table Body */}
            <TableBody className="[&_tr:last-child]:border-l-2">
              {data.map((item, index) => {
                const itemName = item.item || ''
                const isExpanded = isRowExpanded(itemName)
                const hasExpandedData = expandedItems[itemName]

                return (
                  <>
                    {/* Main Item Row */}
                    <TableRow
                      key={`${item.item}-${index}`}
                      className={`border-l-2 ${colors.border.isChild} bg-gray-25 ${
                        isThreeLevel ? 'cursor-pointer hover:bg-gray-50' : ''
                      }`}
                      onClick={
                        isThreeLevel
                          ? (e) => handleExpandClick(item, e)
                          : undefined
                      }
                    >
                      {/* Item Name Cell */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {isThreeLevel && (
                            <div className="flex items-center">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-gray-500" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-gray-500" />
                              )}
                            </div>
                          )}
                          <div
                            className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-semibold ${colors.icon.bg} ${colors.icon.text}`}
                          >
                            {String(item.item ?? '?').slice(0, 1)}
                          </div>
                          <span className="text-md font-medium text-gray-900">
                            {item.item}
                          </span>
                          {getSubItemAlertCount &&
                            item.item &&
                            (() => {
                              const alertCount = getSubItemAlertCount(item.item)
                              return alertCount.total > 0 ? (
                                <span className="inline-block w-2 h-2 bg-red-500 rounded-full"></span>
                              ) : null
                            })()}
                        </div>
                      </TableCell>

                      {/* Metric Cells */}
                      {allMetricKeys.map((metricKey) => {
                        const value = item.current
                          ? (item.current as any)[metricKey]
                          : undefined
                        const prevValue = item.prev
                          ? (item.prev as any)[metricKey]
                          : undefined
                        const metricType = getMetricType(metricKey) as
                          | 'money'
                          | 'percentage'
                          | 'number'
                          | 'hour'

                        return (
                          <TableCell key={metricKey}>
                            {value !== undefined ? (
                              <div className="flex items-center gap-2">
                                <span className="font-medium">
                                  {formatNumber(value, metricType)}
                                </span>
                                {prevValue !== undefined && (
                                  <TrendLabel
                                    oldValue={prevValue}
                                    newValue={value}
                                    trendType={metricType}
                                    verySmall={true}
                                  />
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                        )
                      })}

                      {/* Details Button Cell or Empty Cell for three-level */}
                      <TableCell>
                        {!isThreeLevel ? (
                          <Button
                            variant="outline"
                            onClick={(e) =>
                              handleDetailClick(item.item || '', e)
                            }
                            disabled={loading}
                          >
                            {t('table.detail')}
                            <ChevronRight className="h-3 w-3 ml-1" />
                          </Button>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Expanded SKU Rows (Level 3) */}
                    {isThreeLevel && isExpanded && hasExpandedData && (
                      <>
                        {hasExpandedData.map((skuItem, skuIndex) => (
                          <TableRow
                            key={`${itemName}-sku-${skuIndex}`}
                            className="border-l-2 border-l-gray-200 bg-gray-50"
                          >
                            {/* SKU Name Cell with indentation */}
                            <TableCell>
                              <div className="flex items-center gap-2 pl-8">
                                <div
                                  className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-semibold ${colors.icon.bg} ${colors.icon.text}`}
                                >
                                  {String(skuItem.item ?? '?').slice(0, 1)}
                                </div>
                                <span className="text-sm font-medium text-gray-800">
                                  {skuItem.item}
                                </span>
                                {getSubItemAlertCount &&
                                  skuItem.item &&
                                  (() => {
                                    const alertCount = getSubItemAlertCount(
                                      skuItem.item
                                    )
                                    return alertCount.total > 0 ? (
                                      <span className="inline-block w-2 h-2 bg-red-500 rounded-full"></span>
                                    ) : null
                                  })()}
                              </div>
                            </TableCell>

                            {/* SKU Metric Cells */}
                            {allMetricKeys.map((metricKey) => {
                              const value = skuItem.current
                                ? (skuItem.current as any)[metricKey]
                                : undefined
                              const prevValue = skuItem.prev
                                ? (skuItem.prev as any)[metricKey]
                                : undefined
                              const metricType = getMetricType(metricKey) as
                                | 'money'
                                | 'percentage'
                                | 'number'
                                | 'hour'

                              return (
                                <TableCell key={metricKey}>
                                  {value !== undefined ? (
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-sm">
                                        {formatNumber(value, metricType)}
                                      </span>
                                      {prevValue !== undefined && (
                                        <TrendLabel
                                          oldValue={prevValue}
                                          newValue={value}
                                          trendType={metricType}
                                          verySmall={true}
                                        />
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-sm">
                                      -
                                    </span>
                                  )}
                                </TableCell>
                              )
                            })}

                            {/* SKU Details Button */}
                            <TableCell>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) =>
                                  handleDetailClick(
                                    item.item || '',
                                    e,
                                    skuItem.item || ''
                                  )
                                }
                                disabled={loading}
                              >
                                {t('table.detail')}
                                <ChevronRight className="h-3 w-3 ml-1" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}
                  </>
                )
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  )
}
